import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Paginator } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import WorkTrackingFeedback from "../../components/WorkTracking/WorkTrackingFeedback";
import { useAuth } from "../../context/AuthContext";
import WorkTrackingAnalyticsService from "../../services/WorkTrackingAnalyticsService";
import WorkTrackingService from "../../services/WorkTrackingService";

const PERIOD_OPTIONS = [
  { label: "Öncelikli işler", value: "priority" },
  { label: "Gecikmiş", value: "overdue" },
  { label: "Bugün", value: "today" },
  { label: "Önümüzdeki 7 gün", value: "seven_days" },
  { label: "Önümüzdeki 30 gün", value: "thirty_days" },
  { label: "Sorunlu evrak", value: "problem" },
];

const SORT_OPTIONS = [
  { label: "En acil", value: "urgency" },
  { label: "Vadesi en yakın", value: "due_date" },
  { label: "Para birimi içinde en yüksek", value: "amount_desc" },
  { label: "Para birimi içinde en düşük", value: "amount_asc" },
];

const CATEGORY_TABS = [
  {
    label: "Tümü",
    value: "all",
    icon: "pi pi-th-large",
    countKey: null,
    active: "bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-lg shadow-slate-900/25 border-slate-800 dark:from-white dark:via-slate-100 dark:to-white dark:text-slate-950 dark:border-white dark:shadow-white/10",
    inactive: "bg-slate-100/90 text-slate-700 border-slate-200/80 hover:bg-slate-200/90 hover:text-slate-900 dark:bg-slate-900/60 dark:text-slate-300 dark:border-slate-800 dark:hover:bg-slate-800",
    iconActive: "bg-white/20 text-white dark:bg-slate-950/20 dark:text-slate-950",
    iconInactive: "bg-slate-200/80 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    badgeActive: "bg-white/20 text-white dark:bg-slate-950/20 dark:text-slate-950",
    badgeInactive: "bg-slate-200/90 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  },
  {
    label: "Tahsilatlar",
    value: "collection",
    icon: "pi pi-arrow-down-left",
    countKey: "collectionCount",
    active: "bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30 border-emerald-500 dark:shadow-emerald-950/50",
    inactive: "bg-emerald-50/80 text-emerald-800 border-emerald-200/70 hover:bg-emerald-100 hover:text-emerald-950 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/50 dark:hover:bg-emerald-900/50",
    iconActive: "bg-white/20 text-white",
    iconInactive: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300",
    badgeActive: "bg-white/20 text-white",
    badgeInactive: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300",
  },
  {
    label: "Ödemeler",
    value: "payment",
    icon: "pi pi-arrow-up-right",
    countKey: "paymentCount",
    active: "bg-gradient-to-r from-rose-600 via-rose-500 to-pink-600 text-white shadow-lg shadow-rose-500/30 border-rose-500 dark:shadow-rose-950/50",
    inactive: "bg-rose-50/80 text-rose-800 border-rose-200/70 hover:bg-rose-100 hover:text-rose-950 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900/50 dark:hover:bg-rose-900/50",
    iconActive: "bg-white/20 text-white",
    iconInactive: "bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300",
    badgeActive: "bg-white/20 text-white",
    badgeInactive: "bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-300",
  },
  {
    label: "Çek ve Senetler",
    value: "document",
    icon: "pi pi-file",
    countKey: "documentCount",
    active: "bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30 border-blue-500 dark:shadow-blue-950/50",
    inactive: "bg-blue-50/80 text-blue-800 border-blue-200/70 hover:bg-blue-100 hover:text-blue-950 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900/50 dark:hover:bg-blue-900/50",
    iconActive: "bg-white/20 text-white",
    iconInactive: "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300",
    badgeActive: "bg-white/20 text-white",
    badgeInactive: "bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300",
  },
  {
    label: "Kart Taksitleri",
    value: "installment",
    icon: "pi pi-credit-card",
    countKey: "installmentCount",
    active: "bg-gradient-to-r from-violet-600 via-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30 border-violet-500 dark:shadow-violet-950/50",
    inactive: "bg-violet-50/80 text-violet-800 border-violet-200/70 hover:bg-violet-100 hover:text-violet-950 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-900/50 dark:hover:bg-violet-900/50",
    iconActive: "bg-white/20 text-white",
    iconInactive: "bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300",
    badgeActive: "bg-white/20 text-white",
    badgeInactive: "bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-300",
  },
  {
    label: "Stok",
    value: "stock",
    icon: "pi pi-box",
    countKey: "criticalStockCount",
    active: "bg-gradient-to-r from-amber-500 via-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/30 border-amber-500 dark:shadow-amber-950/50",
    inactive: "bg-amber-50/80 text-amber-900 border-amber-200/70 hover:bg-amber-100 hover:text-amber-950 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/50 dark:hover:bg-amber-900/50",
    iconActive: "bg-white/20 text-white",
    iconInactive: "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300",
    badgeActive: "bg-white/20 text-white",
    badgeInactive: "bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-300",
  },
];

const GROUPS = [
  {
    key: "PROBLEM",
    title: "Sorunlu Evraklar",
    description: "İade veya karşılıksız durumu nedeniyle kontrol bekleyen kayıtlar",
    icon: "pi pi-exclamation-circle",
    accent: "rose",
  },
  {
    key: "OVERDUE",
    title: "Gecikmişler",
    description: "Vadesi geçmiş ve henüz kapanmamış işler",
    icon: "pi pi-clock",
    accent: "rose",
  },
  {
    key: "TODAY",
    title: "Bugün",
    description: "Bugün aksiyon alınması gereken işler",
    icon: "pi pi-calendar",
    accent: "amber",
  },
  {
    key: "UPCOMING",
    title: "Yaklaşanlar",
    description: "Önümüzdeki 7 gün içinde vadesi gelecek işler",
    icon: "pi pi-calendar-plus",
    accent: "blue",
  },
  {
    key: "CRITICAL_STOCK",
    title: "Kritik Stoklar",
    description: "Kritik seviyeye ulaşan veya altına düşen ürünler",
    icon: "pi pi-box",
    accent: "violet",
  },
  {
    key: "PLANNED",
    title: "Planlananlar",
    description: "8–30 gün içinde gündeme gelecek işler",
    icon: "pi pi-calendar-clock",
    accent: "slate",
  },
];

const GROUP_ACCENTS = {
  rose: {
    icon: "bg-rose-100 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400",
    border: "border-l-rose-500",
    badge: "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300",
  },
  amber: {
    icon: "bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400",
    border: "border-l-amber-500",
    badge: "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  },
  blue: {
    icon: "bg-blue-100 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400",
    border: "border-l-blue-500",
    badge: "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
  },
  violet: {
    icon: "bg-violet-100 text-violet-600 dark:bg-violet-950/50 dark:text-violet-400",
    border: "border-l-violet-500",
    badge: "bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
  },
  slate: {
    icon: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    border: "border-l-slate-400",
    badge: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  },
};

const CATEGORY_CARD_ACCENTS = {
  collection: {
    cardBg: "bg-emerald-50/70 border-emerald-200 hover:border-emerald-300 dark:bg-emerald-950/25 dark:border-emerald-900/60 dark:hover:border-emerald-700",
    border: "border-l-emerald-500",
    iconBox: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300",
    categoryTag: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300",
    button: "!bg-emerald-600 hover:!bg-emerald-700 text-white",
  },
  payment: {
    cardBg: "bg-rose-50/70 border-rose-200 hover:border-rose-300 dark:bg-rose-950/25 dark:border-rose-900/60 dark:hover:border-rose-700",
    border: "border-l-rose-500",
    iconBox: "bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300",
    categoryTag: "bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-300",
    button: "!bg-rose-600 hover:!bg-rose-700 text-white",
  },
  document: {
    cardBg: "bg-blue-50/70 border-blue-200 hover:border-blue-300 dark:bg-blue-950/25 dark:border-blue-900/60 dark:hover:border-blue-700",
    border: "border-l-blue-500",
    iconBox: "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300",
    categoryTag: "bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300",
    button: "!bg-blue-600 hover:!bg-blue-700 text-white",
  },
  installment: {
    cardBg: "bg-violet-50/70 border-violet-200 hover:border-violet-300 dark:bg-violet-950/25 dark:border-violet-900/60 dark:hover:border-violet-700",
    border: "border-l-violet-500",
    iconBox: "bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300",
    categoryTag: "bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-300",
    button: "!bg-violet-600 hover:!bg-violet-700 text-white",
  },
  stock: {
    cardBg: "bg-amber-50/70 border-amber-200 hover:border-amber-300 dark:bg-amber-950/25 dark:border-amber-900/60 dark:hover:border-amber-700",
    border: "border-l-amber-500",
    iconBox: "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300",
    categoryTag: "bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-300",
    button: "!bg-amber-600 hover:!bg-amber-700 text-white",
  },
  default: {
    cardBg: "bg-slate-50/80 border-slate-200 hover:border-slate-300 dark:bg-slate-900 dark:border-slate-800 dark:hover:border-slate-700",
    border: "border-l-slate-400",
    iconBox: "bg-slate-200/80 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    categoryTag: "bg-slate-200/80 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    button: "!bg-blue-600 hover:!bg-blue-700 text-white",
  },
};

const getCategoryKey = (item) => {
  const cat = String(item.category || "").toLowerCase();
  if (["collection", "payment", "document", "installment", "stock"].includes(cat)) {
    return cat;
  }
  switch (item.sourceType) {
    case "CUSTOMER_RECEIVABLE":
      return "collection";
    case "SUPPLIER_PAYABLE":
      return "payment";
    case "CHEQUE":
    case "PROMISSORY_NOTE":
      return "document";
    case "CREDIT_CARD_INSTALLMENT":
      return "installment";
    case "CRITICAL_STOCK":
      return "stock";
    default:
      return "default";
  }
};

const SOURCE_META = {
  CUSTOMER_RECEIVABLE: { label: "Müşteri alacağı", icon: "pi pi-users", color: "text-emerald-600" },
  SUPPLIER_PAYABLE: { label: "Tedarikçi borcu", icon: "pi pi-truck", color: "text-rose-600" },
  CHEQUE: { label: "Çek", icon: "pi pi-id-card", color: "text-blue-600" },
  PROMISSORY_NOTE: { label: "Senet", icon: "pi pi-file", color: "text-indigo-600" },
  CREDIT_CARD_INSTALLMENT: { label: "Kart taksiti", icon: "pi pi-credit-card", color: "text-violet-600" },
  CRITICAL_STOCK: { label: "Kritik stok", icon: "pi pi-box", color: "text-amber-600" },
  UNKNOWN: { label: "İş", icon: "pi pi-check-square", color: "text-slate-600" },
};

const ACTION_META = {
  COLLECT_PAYMENT: { label: "Tahsilat Gir", icon: "pi pi-wallet" },
  RECEIVE_PAYMENT: { label: "Tahsilat Gir", icon: "pi pi-wallet" },
  MAKE_PAYMENT: { label: "Ödeme Yap", icon: "pi pi-money-bill" },
  PAY_SUPPLIER: { label: "Ödeme Yap", icon: "pi pi-money-bill" },
  VIEW_CHEQUE: { label: "Çeki Gör", icon: "pi pi-id-card" },
  VIEW_PROMISSORY_NOTE: { label: "Senedi Gör", icon: "pi pi-file" },
  PAY_INSTALLMENT: { label: "Taksiti Öde", icon: "pi pi-credit-card" },
  PROCURE_STOCK: { label: "Tedarik Et", icon: "pi pi-shopping-cart" },
  SUPPLY_STOCK: { label: "Tedarik Et", icon: "pi pi-shopping-cart" },
  RESTOCK: { label: "Tedarik Et", icon: "pi pi-shopping-cart" },
  VIEW_DETAILS: { label: "Detayı Gör", icon: "pi pi-arrow-right" },
};

const STATUS_LABELS = {
  OPEN: "Açık",
  PARTIALLY_COLLECTED: "Kısmen tahsil edildi",
  PARTIALLY_PAID: "Kısmen ödendi",
  PORTFOLIO: "Portföyde",
  ENDORSED: "Tedarikçiye verildi",
  RETURNED: "İade edildi",
  UNPAID: "Ödenmedi",
  CRITICAL: "Kritik",
};

const DEFAULT_FILTERS = {
  period: "priority",
  category: "all",
  search: "",
  sort: "urgency",
  page: 1,
  pageSize: 20,
};

const safePositiveInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getFilters = (searchParams) => {
  const validPeriod = PERIOD_OPTIONS.some((option) => option.value === searchParams.get("period"));
  const validCategory = CATEGORY_TABS.some((option) => option.value === searchParams.get("category"));
  const validSort = SORT_OPTIONS.some((option) => option.value === searchParams.get("sort"));

  return {
    period: validPeriod ? searchParams.get("period") : DEFAULT_FILTERS.period,
    category: validCategory ? searchParams.get("category") : DEFAULT_FILTERS.category,
    search: searchParams.get("search") || DEFAULT_FILTERS.search,
    sort: validSort ? searchParams.get("sort") : DEFAULT_FILTERS.sort,
    page: safePositiveInteger(searchParams.get("page"), DEFAULT_FILTERS.page),
    pageSize: DEFAULT_FILTERS.pageSize,
  };
};

const formatMoney = (amount, currencyCode = "TRY") => {
  if (amount === null || amount === undefined) return null;

  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: currencyCode || "TRY",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${Number(amount).toLocaleString("tr-TR")} ${currencyCode || "TRY"}`;
  }
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
};

const getTimingLabel = (item) => {
  if (item.urgency === "PROBLEM") return "Kontrol bekliyor";
  if (item.urgency === "CRITICAL_STOCK") return "Kritik seviyede";
  if (item.daysRemaining === null) return STATUS_LABELS[item.status] || item.status || "Planlandı";
  if (item.daysRemaining < 0) return `${Math.abs(item.daysRemaining)} gün gecikmiş`;
  if (item.daysRemaining === 0) return "Bugün";
  return `${item.daysRemaining} gün kaldı`;
};

const sumItemsByCurrency = (items) => {
  const totals = new Map();

  items.forEach((item) => {
    const amount = Number(item.amount);
    if (!Number.isFinite(amount) || amount <= 0 || !["INCOMING", "OUTGOING"].includes(item.direction)) return;

    const currencyCode = String(item.currencyCode || "TRY").trim().toUpperCase();
    const current = totals.get(currencyCode) || { currencyCode, incomingAmount: 0, outgoingAmount: 0 };
    if (item.direction === "INCOMING") current.incomingAmount += amount;
    if (item.direction === "OUTGOING") current.outgoingAmount += amount;
    totals.set(currencyCode, current);
  });

  return [...totals.values()].sort((left, right) => left.currencyCode.localeCompare(right.currencyCode));
};

const getFallbackUrl = (item) => {
  const sourceId = item.sourceId ?? item.relatedEntityId;
  const relatedId = item.relatedEntityId ?? item.sourceId;
  const encodedSourceId = sourceId !== undefined && sourceId !== null ? encodeURIComponent(sourceId) : null;
  const encodedRelatedId = relatedId !== undefined && relatedId !== null ? encodeURIComponent(relatedId) : null;

  switch (item.sourceType) {
    case "CUSTOMER_RECEIVABLE":
      return encodedRelatedId ? `/customers/detail/${encodedRelatedId}` : "/customers";
    case "SUPPLIER_PAYABLE":
      return encodedRelatedId ? `/suppliers/detail/${encodedRelatedId}` : "/suppliers";
    case "CHEQUE":
      return encodedSourceId ? `/finance/cheques?highlight=${encodedSourceId}` : "/finance/cheques";
    case "PROMISSORY_NOTE":
      return encodedSourceId ? `/finance/promissory-notes?highlight=${encodedSourceId}` : "/finance/promissory-notes";
    case "CREDIT_CARD_INSTALLMENT":
      return encodedSourceId ? `/finance/installments?highlight=${encodedSourceId}` : "/finance/installments";
    case "CRITICAL_STOCK":
      return encodedSourceId ? `/stock/critical?productId=${encodedSourceId}` : "/stock/critical";
    default:
      return null;
  }
};

function SummaryCard({ title, icon, count, amountsByCurrency = [], tone, active, onClick, footer }) {
  const tones = {
    rose: "from-rose-500 to-rose-600 shadow-rose-200/60 dark:shadow-none",
    amber: "from-amber-500 to-orange-500 shadow-amber-200/60 dark:shadow-none",
    blue: "from-blue-500 to-indigo-600 shadow-blue-200/60 dark:shadow-none",
    indigo: "from-indigo-500 to-violet-600 shadow-indigo-200/60 dark:shadow-none",
    violet: "from-violet-500 to-purple-600 shadow-violet-200/60 dark:shadow-none",
    slate: "from-slate-700 to-slate-900 shadow-slate-200/60 dark:shadow-none",
  };

  const hasAmounts = amountsByCurrency.some((entry) => entry.incomingAmount > 0 || entry.outgoingAmount > 0);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`relative min-h-[132px] overflow-hidden rounded-2xl !border-0 bg-gradient-to-br ${tones[tone]} p-4 text-left text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-200 dark:focus:ring-blue-900 ${active ? "ring-4 ring-blue-200 dark:ring-blue-800" : ""}`}
    >
      <div className="absolute -right-5 -top-6 h-24 w-24 rounded-full bg-white/10" />
      <div className="relative flex h-full flex-col justify-between gap-3">
        <div className="flex items-start justify-between gap-3">
          <span className="text-xs font-black uppercase tracking-wider text-white/80">{title}</span>
          <span className="flex !h-9 !w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
            <i className={`${icon} text-base`} />
          </span>
        </div>
        <div>
          <div className="flex items-baseline gap-2">
            <strong className="text-3xl font-black leading-none">{count.toLocaleString("tr-TR")}</strong>
            <span className="text-xs font-bold text-white/75">işlem</span>
          </div>
          {hasAmounts ? (
            <div className="mt-2 space-y-1 text-[11px] font-bold text-white/85">
              {amountsByCurrency.map((entry) => (
                <div key={entry.currencyCode} className="flex flex-wrap gap-x-3 gap-y-0.5">
                  {entry.incomingAmount > 0 && <span>Alacak {formatMoney(entry.incomingAmount, entry.currencyCode)}</span>}
                  {entry.outgoingAmount > 0 && <span>Ödeme {formatMoney(entry.outgoingAmount, entry.currencyCode)}</span>}
                </div>
              ))}
            </div>
          ) : (
            <p className="m-0 mt-2 text-[11px] font-bold text-white/70">{footer}</p>
          )}
        </div>
      </div>
    </button>
  );
}

function WorkItemCard({ item, onNavigate }) {
  const sourceMeta = SOURCE_META[item.sourceType] || SOURCE_META.UNKNOWN;
  const actionMeta = ACTION_META[item.primaryAction?.type] || ACTION_META.VIEW_DETAILS;
  const hasPrimaryAction = item.primaryAction?.type !== "NONE";
  const fallbackUrl = hasPrimaryAction ? getFallbackUrl(item) : null;
  const primaryUrl = hasPrimaryAction ? item.primaryAction?.url || item.detailUrl || fallbackUrl : null;
  const detailUrl = hasPrimaryAction ? item.detailUrl || fallbackUrl : null;
  const actionLabel = item.primaryAction?.label || actionMeta.label;
  const group = GROUPS.find((entry) => entry.key === item.urgency) || GROUPS[GROUPS.length - 1];
  const categoryKey = getCategoryKey(item);
  const catAccent = CATEGORY_CARD_ACCENTS[categoryKey] || CATEGORY_CARD_ACCENTS.default;
  const urgencyAccent = GROUP_ACCENTS[group.accent] || GROUP_ACCENTS.slate;
  const amount = formatMoney(item.amount, item.currencyCode);
  const dueDate = formatDate(item.dueDate);
  const showSeparateDetail = detailUrl && primaryUrl && detailUrl !== primaryUrl;

  return (
    <article className={`rounded-2xl border border-l-4 p-4 shadow-sm transition-all duration-200 hover:shadow-md ${catAccent.cardBg} ${catAccent.border}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className={`flex !h-11 !w-11 shrink-0 items-center justify-center rounded-xl ${catAccent.iconBox}`}>
            <i className={`${sourceMeta.icon} text-lg`} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${catAccent.categoryTag}`}>
                {sourceMeta.label}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${urgencyAccent.badge}`}>
                {getTimingLabel(item)}
              </span>
              {item.direction === "INCOMING" && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-black text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">Tahsil edilecek</span>
              )}
              {item.direction === "OUTGOING" && (
                <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-black text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">Ödenecek</span>
              )}
            </div>
            <h3 className="m-0 truncate text-sm font-black text-slate-900 dark:text-white sm:text-base" title={item.title}>
              {item.title}
            </h3>
            {item.description && (
              <p className="m-0 mt-1 line-clamp-2 text-xs font-medium leading-relaxed text-slate-500 dark:text-slate-400">
                {item.description}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] font-bold text-slate-500 dark:text-slate-400">
              {dueDate && (
                <span className="flex items-center gap-1.5">
                  <i className="pi pi-calendar text-[10px]" /> Vade: {dueDate}
                </span>
              )}
              {item.branchName && (
                <span className="flex items-center gap-1.5">
                  <i className="pi pi-map-marker text-[10px]" /> {item.branchName}
                </span>
              )}
              {item.relatedEntityName && item.relatedEntityName !== item.title && (
                <span className="flex items-center gap-1.5">
                  <i className="pi pi-link text-[10px]" /> {item.relatedEntityName}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 pt-3 dark:border-slate-800 lg:min-w-[300px] lg:justify-end lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
          <div className="min-w-0 lg:text-right">
            {amount ? (
              <strong className={`block whitespace-nowrap text-base font-black sm:text-lg ${item.direction === "INCOMING" ? "text-emerald-600 dark:text-emerald-400" : item.direction === "OUTGOING" ? "text-rose-600 dark:text-rose-400" : "text-slate-900 dark:text-white"}`}>
                {amount}
              </strong>
            ) : (
              <strong className="block text-sm font-black text-slate-700 dark:text-slate-200">Aksiyon bekliyor</strong>
            )}
            {item.status && <span className="block max-w-[170px] truncate text-[10px] font-bold uppercase text-slate-400">{STATUS_LABELS[item.status] || item.status}</span>}
          </div>

          <div className="flex items-center gap-2">
            {showSeparateDetail && (
              <Button
                type="button"
                icon="pi pi-eye"
                rounded
                text
                severity="secondary"
                aria-label="Detayı görüntüle"
                tooltip="Detayı görüntüle"
                tooltipOptions={{ position: "top" }}
                onClick={() => onNavigate(detailUrl, item, "detail_click")}
                className="!h-10 !w-10"
              />
            )}
            {hasPrimaryAction && (
              <Button
                type="button"
                label={actionLabel}
                icon={actionMeta.icon}
                iconPos="right"
                disabled={!primaryUrl}
                onClick={() => onNavigate(primaryUrl, item, "primary_action_click")}
                className={`!whitespace-nowrap !rounded-xl !border-0 !px-3 !py-2.5 !text-xs !font-black sm:!px-4 ${catAccent.button}`}
              />
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function WorkListSkeleton() {
  return (
    <div className="space-y-3" aria-label="İşler yükleniyor">
      {[1, 2, 3, 4].map((item) => (
        <div key={item} className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-3">
            <Skeleton width="2.75rem" height="2.75rem" borderRadius="0.75rem" />
            <div className="flex-1">
              <Skeleton width="8rem" height="0.7rem" className="mb-2" />
              <Skeleton width="55%" height="1rem" className="mb-2" />
              <Skeleton width="35%" height="0.7rem" />
            </div>
            <Skeleton width="7rem" height="2.5rem" borderRadius="0.75rem" className="hidden sm:block" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function WorkTrackingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesUser = user?.UserRole === "Sales";
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const filters = useMemo(() => getFilters(new URLSearchParams(queryString)), [queryString]);
  const [searchText, setSearchText] = useState(filters.search);
  const [data, setData] = useState({
    summary: {},
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_FILTERS.pageSize,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const requestSequence = useRef(0);

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };
  const requestController = useRef(null);
  const pageViewTracked = useRef(false);

  useEffect(() => {
    setSearchText(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (pageViewTracked.current) return;

    pageViewTracked.current = true;
    void WorkTrackingAnalyticsService.track({
      eventType: "page_view",
      selectedPeriod: filters.period,
      selectedCategory: filters.category,
    });
  }, [filters.category, filters.period]);

  const updateFilters = useCallback((patch, analyticsEventType = "filter_apply") => {
    const next = new URLSearchParams(searchParams);
    const merged = { ...filters, ...patch };

    void WorkTrackingAnalyticsService.track({
      eventType: analyticsEventType,
      selectedPeriod: merged.period,
      selectedCategory: merged.category,
    });

    ["period", "category", "search", "sort", "page"].forEach((key) => {
      const value = merged[key];
      const defaultValue = DEFAULT_FILTERS[key];
      if (value === undefined || value === null || value === "" || value === defaultValue) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });

    setSearchParams(next, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isSalesUser) return;

    const allowedCategories = ["all", "collection", "stock"];
    if (!allowedCategories.includes(filters.category) || filters.period === "problem") {
      updateFilters({
        category: allowedCategories.includes(filters.category) ? filters.category : "all",
        period: filters.period === "problem" ? "priority" : filters.period,
        page: 1,
      });
    }
  }, [filters.category, filters.period, isSalesUser, updateFilters]);

  const fetchWorkItems = useCallback(async () => {
    const sequence = ++requestSequence.current;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    setLoading(true);
    setError("");

    try {
      const result = await WorkTrackingService.getAll(
        {
          period: filters.period,
          category: filters.category,
          search: filters.search || undefined,
          sort: filters.sort,
          page: filters.page,
          pageSize: filters.pageSize,
        },
        { signal: controller.signal }
      );

      if (sequence !== requestSequence.current) return;
      if (!result.success) {
        throw new Error(result.message || "İşler yüklenemedi.");
      }

      setData(result);
    } catch (fetchError) {
      if (fetchError?.code === "ERR_CANCELED" || fetchError?.name === "CanceledError") return;
      if (sequence !== requestSequence.current) return;
      const message = fetchError?.response?.data?.message || fetchError?.message || "İşler yüklenirken bir sorun oluştu.";
      setError(message);
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [filters.category, filters.page, filters.pageSize, filters.period, filters.search, filters.sort]);

  useEffect(() => {
    fetchWorkItems();
    return () => {
      requestSequence.current += 1;
      requestController.current?.abort();
    };
  }, [fetchWorkItems]);

  const groupedItems = useMemo(() => {
    if (filters.sort !== "urgency") {
      const selectedSort = SORT_OPTIONS.find((option) => option.value === filters.sort);
      return [{
        key: "SORTED",
        title: selectedSort?.label || "Sıralanan İşler",
        description: "Seçtiğiniz sıralamaya göre tüm işler birlikte gösteriliyor",
        icon: "pi pi-sort-alt",
        accent: "slate",
        items: data.items,
      }];
    }

    const knownUrgencies = new Set(GROUPS.map((group) => group.key));
    const groups = GROUPS.map((group) => ({
      ...group,
      items: data.items.filter((item) => item.urgency === group.key),
    })).filter((group) => group.items.length > 0);
    const ungroupedItems = data.items.filter((item) => !knownUrgencies.has(item.urgency));

    if (ungroupedItems.length > 0) {
      groups.push({ ...GROUPS[GROUPS.length - 1], key: "OTHER", title: "Diğer İşler", items: ungroupedItems });
    }

    return groups;
  }, [data.items, filters.sort]);

  const summary = data.summary || {};
  const summaryCards = [
    {
      key: "overdue",
      title: "Gecikmiş",
      icon: "pi pi-clock",
      tone: "rose",
      count: summary.overdueCount || 0,
      amountsByCurrency: summary.overdueAmountsByCurrency || [],
      active: filters.period === "overdue",
      onClick: () => updateFilters({ period: "overdue", page: 1 }, "summary_card_click"),
    },
    {
      key: "today",
      title: "Bugün",
      icon: "pi pi-calendar",
      tone: "amber",
      count: summary.todayCount || 0,
      amountsByCurrency: summary.todayAmountsByCurrency || [],
      active: filters.period === "today",
      onClick: () => updateFilters({ period: "today", page: 1 }, "summary_card_click"),
    },
    {
      key: "seven-days",
      title: "7 Gün İçinde",
      icon: "pi pi-calendar-plus",
      tone: "blue",
      count: summary.sevenDayCount || 0,
      amountsByCurrency: summary.sevenDayAmountsByCurrency || [],
      active: filters.period === "seven_days",
      onClick: () => updateFilters({ period: "seven_days", page: 1 }, "summary_card_click"),
    },
    {
      key: "thirty-days",
      title: "30 Gün İçinde",
      icon: "pi pi-calendar-clock",
      tone: "indigo",
      count: summary.thirtyDayCount || 0,
      amountsByCurrency: summary.thirtyDayAmountsByCurrency || [],
      active: filters.period === "thirty_days",
      onClick: () => updateFilters({ period: "thirty_days", page: 1 }, "summary_card_click"),
    },
    {
      key: "critical-stock",
      title: "Kritik Stok",
      icon: "pi pi-box",
      tone: "violet",
      count: summary.criticalStockCount || 0,
      amountsByCurrency: [],
      footer: "Tedarik bekleyen ürün",
      active: filters.category === "stock",
      onClick: () => updateFilters({ period: "priority", category: "stock", page: 1 }, "summary_card_click"),
    },
    {
      key: "problem-document",
      title: "Sorunlu Evrak",
      icon: "pi pi-exclamation-circle",
      tone: "slate",
      count: summary.problemDocumentCount || 0,
      amountsByCurrency: [],
      footer: "Kontrol bekleyen çek veya senet",
      active: filters.category === "document" && filters.period === "problem",
      onClick: () => updateFilters({ period: "problem", category: "document", page: 1 }, "summary_card_click"),
    },
  ];
  const visibleSummaryCards = isSalesUser
    ? summaryCards.filter((card) => card.key !== "problem-document")
    : summaryCards;
  const visibleCategoryTabs = isSalesUser
    ? CATEGORY_TABS.filter((tab) => ["all", "collection", "stock"].includes(tab.value))
    : CATEGORY_TABS;
  const visiblePeriodOptions = isSalesUser
    ? PERIOD_OPTIONS.filter((option) => option.value !== "problem")
    : PERIOD_OPTIONS;

  const handleCategoryChange = (category) => {
    let period = filters.period;
    if (category === "stock") period = "priority";
    if (period === "problem" && !["all", "document"].includes(category)) period = "priority";
    updateFilters({ category, period, page: 1 }, "tab_select");
  };

  const handlePeriodChange = (period) => {
    let category = filters.category;
    if (period === "problem") category = "document";
    if (category === "stock" && period !== "priority") category = "all";
    updateFilters({ period, category, page: 1 });
  };

  const handleSearch = (event) => {
    event.preventDefault();
    updateFilters({ search: searchText.trim(), page: 1 }, "search");
  };

  const handleNavigate = (url, item, eventType = "primary_action_click") => {
    if (!url) return;

    void WorkTrackingAnalyticsService.track({
      eventType,
      workItemType: item.category,
      sourceType: item.sourceType,
      selectedPeriod: filters.period,
      selectedCategory: filters.category,
    });

    if (/^https?:\/\//i.test(url)) {
      window.location.assign(url);
      return;
    }

    navigate(url, {
      state: {
        fromWorkTracking: true,
        workItemId: item.id,
        sourceId: item.sourceId,
      },
    });
  };

  const clearFilters = () => {
    setSearchText("");
    setSearchParams({}, { replace: true });
  };

  return (
    <div className="space-y-5 pb-8 animate-fade-in">
      <section className="relative overflow-hidden rounded-[2rem] border border-slate-800 bg-slate-950 p-5 text-white shadow-xl sm:p-6">
        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-violet-600/10 blur-3xl" />
        <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex !h-14 !w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-600 shadow-lg shadow-blue-900/40 sm:!h-16 sm:!w-16">
              <i className="pi pi-check-square text-2xl text-white" />
            </div>
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-blue-400/20 bg-blue-500/15 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-blue-300">
                  Günlük çalışma merkezi
                </span>
                <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-300">Yeni</span>
              </div>
              <h1 className="m-0 text-2xl font-black tracking-tight text-white sm:text-3xl">İş Takibi</h1>
              <p className="m-0 mt-1 max-w-2xl text-xs font-medium leading-relaxed text-slate-400 sm:text-sm">
                Tahsilat, ödeme, evrak, taksit ve stok işlerinizi aciliyet sırasıyla tek noktadan takip edin.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-700/70 bg-slate-900/80 p-3 md:justify-end">
            <div className="flex items-center gap-2.5">
              <span className="flex !h-9 !w-9 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-blue-400">
                <i className="pi pi-map-marker" />
              </span>
              <div>
                <span className="block text-[9px] font-black uppercase tracking-widest text-slate-500">Şube kapsamı</span>
                <span className="block text-xs font-bold text-slate-200">Üst menüde seçili şube</span>
              </div>
            </div>
            <Button
              type="button"
              icon="pi pi-refresh"
              rounded
              text
              loading={loading}
              aria-label="İşleri yenile"
              tooltip="İşleri yenile"
              tooltipOptions={{ position: "bottom" }}
              onClick={fetchWorkItems}
              className="!h-10 !w-10 !text-slate-300 hover:!bg-slate-800"
            />
          </div>
        </div>
      </section>

      <section aria-label="İş özeti" className={`grid grid-cols-2 gap-3 md:grid-cols-3 ${isSalesUser ? "xl:grid-cols-5" : "xl:grid-cols-6"}`}>
        {visibleSummaryCards.map((card) => <SummaryCard key={card.key} {...card} />)}
      </section>

      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="border-b border-slate-200 p-3 dark:border-slate-800 sm:p-4">
          <div className="flex gap-2.5 overflow-x-auto pb-1.5 pt-0.5 custom-scrollbar" role="tablist" aria-label="İş türleri">
            {visibleCategoryTabs.map((tab) => {
              const active = filters.category === tab.value;
              const count = tab.countKey ? data.summary[tab.countKey] : data.totalCount;

              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => handleCategoryChange(tab.value)}
                  className={`group relative flex shrink-0 items-center gap-2.5 rounded-2xl border px-3.5 py-2.5 text-xs font-black transition-all duration-300 hover:-translate-y-0.5 active:scale-95 ${
                    active ? tab.active : tab.inactive
                  }`}
                >
                  <span
                    className={`flex !h-7 !w-7 shrink-0 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110 ${
                      active ? tab.iconActive : tab.iconInactive
                    }`}
                  >
                    <i className={`${tab.icon} text-xs`} />
                  </span>
                  <span className="whitespace-nowrap tracking-tight">{tab.label}</span>
                  {typeof count === "number" && count > 0 && (
                    <span
                      className={`ml-0.5 rounded-full px-2 py-0.5 text-[10px] font-black leading-none transition-colors ${
                        active ? tab.badgeActive : tab.badgeInactive
                      }`}
                    >
                      {count.toLocaleString("tr-TR")}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 bg-slate-50/80 p-3 dark:bg-slate-900/50 sm:p-4 lg:grid-cols-[minmax(220px,1fr)_minmax(260px,1.7fr)_minmax(200px,1fr)_auto] lg:items-end">
          <div>
            <label htmlFor="work-period" className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">Zaman aralığı</label>
            <Dropdown
              inputId="work-period"
              value={filters.period}
              options={visiblePeriodOptions}
              onChange={(event) => handlePeriodChange(event.value)}
              className="w-full !rounded-xl !border-slate-200 dark:!border-slate-700 dark:!bg-slate-900"
            />
          </div>

          <form onSubmit={handleSearch}>
            <label htmlFor="work-search" className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">İşlerde ara</label>
            <div className="flex">
              <span className="p-input-icon-left min-w-0 flex-1">
                <i className="pi pi-search ml-3 text-slate-400" />
                <InputText
                  id="work-search"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Müşteri, tedarikçi, evrak veya ürün ara..."
                  className="w-full !rounded-l-xl !rounded-r-none !border-slate-200 !py-3 !pl-10 text-sm dark:!border-slate-700 dark:!bg-slate-900 dark:!text-white"
                />
              </span>
              <Button
                type="submit"
                icon="pi pi-search"
                aria-label="Ara"
                className="!rounded-l-none !rounded-r-xl !border-0 !bg-blue-600 !px-4 hover:!bg-blue-700"
              />
            </div>
          </form>

          <div>
            <label htmlFor="work-sort" className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">Sıralama</label>
            <Dropdown
              inputId="work-sort"
              value={filters.sort}
              options={SORT_OPTIONS}
              onChange={(event) => updateFilters({ sort: event.value, page: 1 })}
              className="w-full !rounded-xl !border-slate-200 dark:!border-slate-700 dark:!bg-slate-900"
            />
          </div>

          <Button
            type="button"
            label="Temizle"
            icon="pi pi-filter-slash"
            outlined
            severity="secondary"
            onClick={clearFilters}
            className="!rounded-xl !py-3 !text-xs !font-black"
          />
        </div>
      </section>

      <section aria-live="polite">
        {loading ? (
          <WorkListSkeleton />
        ) : error ? (
          <div className="rounded-[2rem] border border-rose-200 bg-white px-6 py-12 text-center shadow-sm dark:border-rose-950 dark:bg-slate-950">
            <div className="mx-auto flex !h-14 !w-14 shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400">
              <i className="pi pi-exclamation-triangle text-xl" />
            </div>
            <h2 className="mb-1 mt-4 text-lg font-black text-slate-900 dark:text-white">İşler yüklenemedi</h2>
            <p className="mx-auto mb-5 max-w-lg text-sm font-medium text-slate-500 dark:text-slate-400">{error}</p>
            <Button label="Tekrar Dene" icon="pi pi-refresh" onClick={fetchWorkItems} className="!rounded-xl !bg-blue-600 !font-black" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="rounded-[2rem] border border-emerald-200 bg-white px-6 py-14 text-center shadow-sm dark:border-emerald-950 dark:bg-slate-950">
            <div className="mx-auto flex !h-16 !w-16 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400">
              <i className="pi pi-check-circle text-2xl" />
            </div>
            <h2 className="mb-1 mt-4 text-lg font-black text-slate-900 dark:text-white">
              {filters.period === "priority" && filters.category === "all" && !filters.search ? "Harika, bekleyen kritik bir işiniz yok!" : "Eşleşen iş bulunamadı"}
            </h2>
            <p className="mx-auto mb-5 max-w-lg text-sm font-medium text-slate-500 dark:text-slate-400">
              {filters.period === "priority" && filters.category === "all" && !filters.search
                ? "Şu anda gecikmiş, bugün aksiyon bekleyen veya yaklaşan bir kaydınız bulunmuyor."
                : "Seçtiğiniz filtreleri veya arama ifadenizi değiştirerek tekrar deneyebilirsiniz."}
            </p>
            {(filters.period !== "priority" || filters.category !== "all" || filters.search) && (
              <Button label="Filtreleri Temizle" icon="pi pi-filter-slash" outlined onClick={clearFilters} className="!rounded-xl !font-black" />
            )}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between px-1">
              <div>
                <h2 className="m-0 text-base font-black text-slate-900 dark:text-white">Aksiyon Bekleyen İşler</h2>
                <p className="m-0 mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                  Toplam {data.totalCount.toLocaleString("tr-TR")} kayıt, aciliyet sırasıyla gösteriliyor.
                </p>
              </div>
            </div>

            {groupedItems.map((group) => {
              const accent = GROUP_ACCENTS[group.accent] || GROUP_ACCENTS.slate;
              const groupAmountsByCurrency = sumItemsByCurrency(group.items);
              const isCollapsed = Boolean(collapsedGroups[group.key]);

              return (
                <div key={group.key} className="space-y-3">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleGroupCollapse(group.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleGroupCollapse(group.key);
                      }
                    }}
                    aria-expanded={!isCollapsed}
                    aria-label={`${group.title} bölümünü ${isCollapsed ? "aç" : "kapat"}`}
                    className="group/header flex cursor-pointer select-none flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`flex !h-10 !w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover/header:scale-105 ${accent.icon}`}>
                        <i className={`${group.icon} text-sm`} />
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="m-0 text-sm font-black text-slate-900 dark:text-white sm:text-base">{group.title}</h3>
                          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-black ${accent.badge}`}>{group.items.length} kayıt</span>
                        </div>
                        <p className="m-0 mt-0.5 hidden text-[11px] font-medium text-slate-500 dark:text-slate-400 sm:block">{group.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {groupAmountsByCurrency.length > 0 && (
                        <div className="flex flex-wrap justify-end gap-x-3 gap-y-1 text-xs font-black text-slate-600 dark:text-slate-300">
                          {groupAmountsByCurrency.flatMap((entry) => [
                            entry.incomingAmount > 0 ? <span key={`${entry.currencyCode}-incoming`}>Alacak {formatMoney(entry.incomingAmount, entry.currencyCode)}</span> : null,
                            entry.outgoingAmount > 0 ? <span key={`${entry.currencyCode}-outgoing`}>Ödeme {formatMoney(entry.outgoingAmount, entry.currencyCode)}</span> : null,
                          ]).filter(Boolean)}
                        </div>
                      )}

                      <span className="flex !h-8 !w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-all duration-200 group-hover/header:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:group-hover/header:bg-slate-700">
                        <i className={`pi pi-chevron-down text-xs transition-transform duration-200 ${isCollapsed ? "-rotate-90" : "rotate-0"}`} />
                      </span>
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="space-y-2.5 animate-fade-in">
                      {group.items.map((item) => <WorkItemCard key={item.id} item={item} onNavigate={handleNavigate} />)}
                    </div>
                  )}
                </div>
              );
            })}

            {data.totalCount > data.pageSize && (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
                <Paginator
                  first={(data.page - 1) * data.pageSize}
                  rows={data.pageSize}
                  totalRecords={data.totalCount}
                  onPageChange={(event) => {
                    updateFilters({ page: event.page + 1 });
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                  currentPageReportTemplate="{first}-{last} / {totalRecords}"
                  className="!border-0 !bg-transparent dark:!text-slate-300"
                />
              </div>
            )}
          </div>
        )}
      </section>
      <WorkTrackingFeedback period={filters.period} category={filters.category} />
    </div>
  );
}
